// Google Tag Manager integration
declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Push custom events to GTM dataLayer
 * @param event - Event name
 * @param data - Additional event data
 */
export const pushDataLayer = (event: string, data?: Record<string, any>): void => {
  if (!window.dataLayer) {
    console.warn('GTM dataLayer not initialized');
    return;
  }

  window.dataLayer.push({
    event,
    ...data
  });
};

/**
 * Track affiliate click events to GTM with guaranteed delivery time
 * Returns a promise that resolves after GTM has had time to process
 * 
 * @param params - Tracking parameters
 * @param minWaitMs - Minimum wait time in milliseconds (default: 300ms)
 * @returns Promise that resolves when tracking is complete
 */
export const trackAffiliateClickGTM = async (
  params: {
    cardId: string;
    cardName: string;
    issuerName: string;
    affiliateUrl: string;
    clickSource: string;
    offerTitle?: string;
    annualFee?: number;
    effectiveRate?: number;
    userId?: string | null;
  },
  minWaitMs: number = 300
): Promise<void> => {
  const startTime = Date.now();
  
  // Push to dataLayer
  pushDataLayer('affiliate_click', {
    event_category: 'affiliate',
    event_label: params.cardName,
    click_source: params.clickSource,
    card_id: params.cardId,
    card_name: params.cardName,
    issuer_name: params.issuerName,
    affiliate_url: params.affiliateUrl,
    has_offer: !!params.offerTitle,
    offer_title: params.offerTitle || null,
    annual_fee: params.annualFee ?? null,
    effective_rate: params.effectiveRate ?? null,
    user_authenticated: !!params.userId,
    timestamp: startTime, // For deduplication in GTM
  });

  console.log(`🔥 GTM event fired for ${params.cardName} from ${params.clickSource}`);

  // Wait minimum time to ensure GTM processes the event
  return new Promise((resolve) => {
    setTimeout(() => {
      const elapsed = Date.now() - startTime;
      console.log(`✅ GTM tracking complete (${elapsed}ms)`);
      resolve();
    }, minWaitMs);
  });
};
